import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import TopBar from '../components/TopBar';
import ConciergeLauncher from '../components/ConciergeLauncher';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { ArrowRight, Bookmark } from 'lucide-react';
import { toast } from 'sonner';

const KnowledgePage = () => {
  const { resources, savePack } = useData();
  const navigate = useNavigate();
  const [wizardComplete, setWizardComplete] = useState(false);
  const [filters, setFilters] = useState({
    types: [],
    technologies: [],
    regions: []
  });

  const aiPackResources = resources.slice(0, 6);

  const handleSavePack = () => {
    const pack = {
      id: `pack-${Date.now()}`,
      title: 'BESS procurement starter pack',
      rationale: 'Built for a utility procuring storage in Sub-Saharan Africa',
      items: aiPackResources.map((r, idx) => ({
        resourceId: r.id,
        ordinal: idx,
        relevance: 98 - (idx * 2)
      }))
    };
    savePack(pack);
    toast.success('Pack saved to your dashboard');
  };

  const filteredResources = resources.filter(r => {
    if (filters.types.length > 0 && !filters.types.includes(r.type)) return false;
    if (filters.technologies.length > 0 && !r.technologies.some(t => filters.technologies.includes(t))) return false;
    if (filters.regions.length > 0 && !r.regions.some(reg => filters.regions.includes(reg))) return false;
    return true;
  });

  const toggleFilter = (category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(v => v !== value)
        : [...prev[category], value]
    }));
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--bg))' }}>
      <TopBar />

      <div className="wrap section">
        {/* Hero with Wizard */}
        <Card className="p-8 mb-8">
          <span className="eyebrow">Knowledge Repository · "Know"</span>
          <h2 className="mt-3 mb-2">Tell us your context. We'll build the pack.</h2>
          <p className="mb-6" style={{ color: 'hsl(var(--ink-muted))' }}>
            Instead of dropping you into 2,400 documents, GridCommons asks three quick questions and assembles a tailored resource pack.
          </p>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-4 rounded-lg" style={{ backgroundColor: 'hsl(var(--surface-2))' }}>
              <div className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: 'hsl(var(--primary))', color: 'white', fontSize: '14px', fontWeight: '600' }}>1</div>
              <div className="flex-1">
                <div className="text-xs font-medium" style={{ color: 'hsl(var(--ink-muted))' }}>I am a</div>
                <div className="text-sm font-semibold">Utility / grid operator</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg" style={{ backgroundColor: 'hsl(var(--surface-2))' }}>
              <div className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: 'hsl(var(--primary))', color: 'white', fontSize: '14px', fontWeight: '600' }}>2</div>
              <div className="flex-1">
                <div className="text-xs font-medium" style={{ color: 'hsl(var(--ink-muted))' }}>I want to</div>
                <div className="text-sm font-semibold">Procure storage (BESS)</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg" style={{ backgroundColor: 'hsl(var(--surface-2))' }}>
              <div className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: 'hsl(var(--primary))', color: 'white', fontSize: '14px', fontWeight: '600' }}>3</div>
              <div className="flex-1">
                <div className="text-xs font-medium" style={{ color: 'hsl(var(--ink-muted))' }}>My region</div>
                <div className="text-sm font-semibold">Sub-Saharan Africa</div>
              </div>
            </div>
          </div>
        </Card>

        {/* AI Pack */}
        <Card className="p-6 mb-8" style={{ borderColor: 'hsl(var(--accent))' }}>
          <span className="text-xs font-semibold" style={{ color: 'hsl(var(--accent-ink))' }}>✦ AI-assembled pack</span>
          <h4 className="mt-2 mb-1">Your BESS procurement starter pack — 6 resources, ordered</h4>
          <p className="text-sm mb-4" style={{ color: 'hsl(var(--ink-muted))' }}>
            Built for a utility procuring storage in Sub-Saharan Africa. Starts with the sizing tool, moves through a model tender, and ends with two peer utilities who've done this.
          </p>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/ask')}>Open in Grid Guide <ArrowRight className="ml-2 h-4 w-4" /></Button>
            <Button variant="outline" onClick={handleSavePack}><Bookmark className="mr-2 h-4 w-4" />Save pack</Button>
          </div>
        </Card>

        {/* Main Layout */}
        <div className="grid md:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <aside className="space-y-6">
            <div>
              <h4 className="text-sm font-semibold mb-3">Resource type</h4>
              <div className="space-y-2">
                {['POLICY', 'PROCUREMENT', 'MARKET_INTEL', 'TOOL'].map(type => (
                  <div key={type} className="flex items-center space-x-2">
                    <Checkbox
                      id={type}
                      checked={filters.types.includes(type)}
                      onCheckedChange={() => toggleFilter('types', type)}
                    />
                    <Label htmlFor={type} className="text-sm cursor-pointer">
                      {type.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-3">Technology</h4>
              <div className="space-y-2">
                {['BESS', 'Mini-grids', 'Grid Digitization'].map(tech => (
                  <div key={tech} className="flex items-center space-x-2">
                    <Checkbox
                      id={tech}
                      checked={filters.technologies.includes(tech)}
                      onCheckedChange={() => toggleFilter('technologies', tech)}
                    />
                    <Label htmlFor={tech} className="text-sm cursor-pointer">{tech}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-3">Region</h4>
              <div className="space-y-2">
                {['Sub-Saharan Africa', 'South Asia', 'Global'].map(region => (
                  <div key={region} className="flex items-center space-x-2">
                    <Checkbox
                      id={region}
                      checked={filters.regions.includes(region)}
                      onCheckedChange={() => toggleFilter('regions', region)}
                    />
                    <Label htmlFor={region} className="text-sm cursor-pointer">{region}</Label>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          {/* Results */}
          <div className="md:col-span-3 space-y-4">
            <div className="flex items-center justify-between text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>
              <span>Showing <strong style={{ color: 'hsl(var(--ink))' }}>{filteredResources.length}</strong> resources</span>
              <span className="font-semibold" style={{ color: 'hsl(var(--primary-700))' }}>Relevance ▾</span>
            </div>

            <div className="space-y-3">
              {filteredResources.map((resource, idx) => (
                <Card
                  key={resource.id}
                  className="p-5 cursor-pointer hover:shadow-lg flex items-center gap-4"
                  onClick={() => navigate(`/knowledge/${resource.id}`)}
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg flex-shrink-0" style={{
                    backgroundColor: resource.type === 'TOOL' ? 'hsl(var(--accent-soft))' : 'hsl(var(--blue-soft))'
                  }}>
                    <span className="text-2xl">{resource.type === 'TOOL' ? '⚡' : '📄'}</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="mb-1">{resource.title}</h4>
                    <p className="text-sm mb-2" style={{ color: 'hsl(var(--ink-muted))' }}>{resource.summary}</p>
                    <div className="flex items-center gap-3 text-xs" style={{ color: 'hsl(var(--ink-faint))' }}>
                      <span className="px-2 py-0.5 rounded" style={{
                        backgroundColor: resource.type === 'TOOL' ? 'hsl(var(--accent-soft))' : 'hsl(var(--blue-soft))',
                        color: resource.type === 'TOOL' ? 'hsl(var(--accent-ink))' : 'hsl(var(--blue))'
                      }}>
                        {resource.type}
                      </span>
                      <span>{resource.sourceOrg}</span>
                      {idx < 6 && (
                        <span className="flex items-center gap-1 ml-auto">
                          <span className="font-semibold" style={{ color: 'hsl(var(--ink-2))' }}>{98 - (idx * 2)}%</span>
                          <div className="w-16 h-1.5 rounded-full" style={{ backgroundColor: 'hsl(var(--line-2))' }}>
                            <div className="h-full rounded-full" style={{ width: `${98 - (idx * 2)}%`, backgroundColor: 'hsl(var(--mint))' }} />
                          </div>
                        </span>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 flex-shrink-0" style={{ color: 'hsl(var(--ink-muted))' }} />
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ConciergeLauncher />
    </div>
  );
};

export default KnowledgePage;
